import { useCallback, useState } from 'react';
import { normalizeWebsiteUrl, WEBSITE_URL_ERROR_TEXT } from '@mg/shared';

/**
 * The Public Website box on the Create page and the Edit page (spec:43, E18), around the shared
 * `normalizeWebsiteUrl`, the same check the API runs. The words come from `WEBSITE_URL_ERROR_TEXT`, so
 * the box and a 400 from the server say the same thing.
 */

/** Why a website value cannot be saved as it stands, in words; null when it can. */
export function websiteError(value: string): string | null {
  const result = normalizeWebsiteUrl(value);
  return result.ok ? null : WEBSITE_URL_ERROR_TEXT[result.error];
}

export interface WebsiteFieldOptions {
  /**
   * Show the error now, even before the box was left: an attempt to save, or (Edit page, O15) a page
   * with changes, where a stored address that is no longer valid must be fixed before saving.
   */
  showErrors?: boolean;
}

export interface WebsiteField {
  /** What the box shows. */
  value: string;
  /** For the box's onChange. */
  setValue: (next: string) => void;
  /**
   * For the box's onBlur. A valid address is rewritten to its normalized `https://` form; an invalid
   * one that is not empty starts showing its error, which then follows the typing until it is fixed.
   */
  onBlur: () => void;
  /** The message to show under the box now, or null. */
  error: string | null;
  /** The address to send (`https://…`), or null while it is not valid. */
  normalized: string | null;
  valid: boolean;
  /** Puts the box back to a value with no error shown (Discard, or a new saved value). */
  reset: (next: string) => void;
}

export function useWebsiteField(initial: string, options: WebsiteFieldOptions = {}): WebsiteField {
  const [value, setRaw] = useState(initial);
  // Set when the person leaves the box with an address that is not valid; cleared once it is.
  const [shown, setShown] = useState(false);

  const result = normalizeWebsiteUrl(value);
  const issue = result.ok ? null : WEBSITE_URL_ERROR_TEXT[result.error];

  const setValue = useCallback((next: string) => {
    setRaw(next);
    if (normalizeWebsiteUrl(next).ok) setShown(false);
  }, []);

  const onBlur = useCallback(() => {
    const now = normalizeWebsiteUrl(value);
    if (now.ok) {
      if (now.url !== value) setRaw(now.url);
      setShown(false);
    } else if (value.trim() !== '') {
      setShown(true);
    }
  }, [value]);

  const reset = useCallback((next: string) => {
    setRaw(next);
    setShown(false);
  }, []);

  return {
    value,
    setValue,
    onBlur,
    error: shown || options.showErrors ? issue : null,
    normalized: result.ok ? result.url : null,
    valid: result.ok,
    reset,
  };
}
